import { Anchor, Flex, Group, Stack, Text } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { EvaluationReportButton } from '@/module/Evaluation/components/EvaluationReportButton';
import { EvaluationReportTables } from '@/module/Evaluation/components/EvaluationReportTables';
import { EvaluationReportZipButton } from '@/module/Evaluation/components/EvaluationReportZipButton';
import { StartEvaluationButton } from '@/module/Evaluation/components/StartEvaluationButton';
import { VerifyEvaluationButton } from '@/module/Evaluation/components/VerifyEvaluationButton';
import { EvaluationAuthorChiefFile } from '@/module/Evaluation/components/files/EvaluationAuthorChiefFile';
import { EvaluationAuthorCollegialFile } from '@/module/Evaluation/components/files/EvaluationAuthorCollegialFile';
import { EvaluationDepartmentAdminSignedFile } from '@/module/Evaluation/components/files/EvaluationDepartmentAdminSignedFile';
import { EvaluationDisciplineAdminSignedFile } from '@/module/Evaluation/components/files/EvaluationDisciplineAdminSignedFile';
import { getAuthorEvaluationOptions } from '@/module/Evaluation/model/EvaluationQuery';
import { EvaluationReport } from '@/module/Evaluation/model/EvaluationTypes';

import { Content, DateText, PendingSpinner } from '@/core/components';

import { RoleType } from '@feature/security';

interface AuthorEvaluationOverviewProps {
  evaluation: EvaluationReport;
  canStartEvaluation: boolean;
  userId: number;
  canDownloadUnsignedReport: boolean;

  canVerifyEvaluation?: boolean;
  currentRole: RoleType;
}

export function AuthorEvaluationOverview(props: AuthorEvaluationOverviewProps) {
  const { evaluation, canStartEvaluation, userId, canVerifyEvaluation, currentRole } = props;
  const { t } = useTranslation();
  const { data } = useSuspenseQuery(getAuthorEvaluationOptions(evaluation.id, userId));
  const isCompleted = data?.completedAt != null;

  return (
    <Stack>
      <Content type="full">
        <Flex
          align="center"
          justify="space-between"
        >
          {data?.user != null ? (
            <Stack gap="4px">
              <Text>
                {data.user.lastName} {data.user.firstName}, {t(`jobTitleEnum.${data.user.jobTitle}`)}
              </Text>
              <Anchor href={`mailto:${data.user.email}`}>{data.user.email}</Anchor>
            </Stack>
          ) : (
            <div />
          )}
          <Stack>
            {canStartEvaluation && !isCompleted && (
              <StartEvaluationButton
                alreadyStarted={data != null}
                evaluationId={evaluation.id}
              />
            )}
            {data != null && canVerifyEvaluation && <VerifyEvaluationButton evaluationId={evaluation.id} />}
          </Stack>
        </Flex>
        {data != null && (
          <Text
            c="dimmed"
            size="sm"
          >
            {t('evaluation.forSpecificOrderNo')}
          </Text>
        )}
      </Content>
      <Content type="full">
        {data == null && (
          <Text
            fw="500"
            c="dimmed"
            fs="italic"
          >
            {t('evaluation.reportingNotStartedYet')}
          </Text>
        )}
        {data != null && (
          <Suspense fallback={<PendingSpinner h="20vh" />}>
            <Flex justify="space-between">
              <Flex>
                <Text mr="10px">{t('evaluation.startedAt')}</Text>
                <DateText
                  showAs="date-time"
                  date={data.startedAt}
                />
              </Flex>
            </Flex>
            <Group>
              {canVerifyEvaluation && (
                <EvaluationReportButton
                  evaluationId={evaluation.id}
                  authorId={userId}
                />
              )}
              {/*{data.completedAt != null && TODO ENABLE IN 2 weeks (*/}
              {/*  <EvaluationReportSignedButton evaluationId={evaluation.id} authorId={userId} />*/}
              {/*)}*/}
              <EvaluationReportZipButton
                evaluationId={evaluation.id}
                authorId={userId}
              />
            </Group>
          </Suspense>
        )}
      </Content>
      {data != null && (
        <Content type="full">
          <Group justify="space-between">
            <EvaluationAuthorCollegialFile
              userId={userId}
              evaluationId={evaluation.id}
              file={data.collegiateEvaluationFile}
            />
            <EvaluationAuthorChiefFile
              userId={userId}
              evaluationId={evaluation.id}
              file={data.chiefEvaluationFile}
            />
          </Group>
        </Content>
      )}
      {data != null && canVerifyEvaluation && (
        <Content type="full">
          <Group>
            <Text
              c="red"
              fw="500"
            >
              Atenție!
            </Text>
            <Text size="sm">
              Documentul urcat în platformă necesită semnatura electronică. Șeful de disciplină va putea semna doar
              documentul semnat deja de șeful de departament.
            </Text>
            <Group justify="space-between">
              <EvaluationDepartmentAdminSignedFile
                userId={userId}
                evaluationId={evaluation.id}
                disabled={currentRole !== 'DEPARTMENT_ADMIN' && currentRole !== 'FACULTY_ADMIN'}
                file={data.departmentAdminSignedFile}
              />
              <EvaluationDisciplineAdminSignedFile
                userId={userId}
                evaluationId={evaluation.id}
                disabled={data.departmentAdminSignedFile == null || currentRole != 'DISCIPLINE_ADMIN'}
                file={data.disciplineAdminSignedFile}
              />
            </Group>
          </Group>
        </Content>
      )}
      {data != null && (
        <EvaluationReportTables
          evaluationId={evaluation.id}
          authorId={userId}
          isEvaluationClosed={!evaluation.open}
        />
      )}
    </Stack>
  );
}
